import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { FaGoogle, FaEye, FaEyeSlash } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { FaCircleChevronLeft } from "react-icons/fa6";
import toast from "react-hot-toast";

const Signup = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    telephone: "",
    password: "",
    confirmPassword: "",
    agree: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});

  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL;

  const { mutate: signUp, isPending, isError, error } = useMutation({
    mutationKey: ["auth"],
    mutationFn: async ({ fullName, email, telephone, password, confirmPassword }) => {
      const res = await fetch(`${API_URL}/auth/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fullName, email, telephone, password, confirmPassword }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "something went wrong");

      Cookies.set("gameport", data.token, {
        expires: 15,
        secure: process.env.NODE_ENV !== "development",
        sameSite: "Strict",
      });

      return data;
    },
    onSuccess: () => {
      toast.success("Account created successfully");
      navigate("/");
    },
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
  };

  const validate = () => {
    const e = {};

    if (!formData.fullName.trim()) e.fullName = "Full name is required";
    if (!formData.email.trim()) e.email = "Email is required";
    if (!formData.telephone.trim()) e.telephone = "Phone number is required";

    if (!formData.password) e.password = "Password is required";
    else if (formData.password.length < 8) e.password = "Password must be at least 8 characters";

    if (!formData.confirmPassword) e.confirmPassword = "Confirm your password";
    else if (formData.password !== formData.confirmPassword)
      e.confirmPassword = "Passwords do not match";

    if (!formData.agree) e.agree = "You must accept the terms";

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    signUp(formData);
  };

  const googleAuth = () => {
    window.open(`${API_URL}/auth/google/callback`, "_self");
  };

  return (
    <div className="min-h-screen w-full bg-slate-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sm:p-8">
        <div className="text-center">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900">
            Sign Up
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Create your account to start shopping
          </p>
        </div>

        <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
          {/* Full Name */}
          <div>
            <label className="text-sm font-semibold text-gray-800">Full Name</label>
            <input
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              placeholder="David Olotu"
              className="w-full mt-1 px-4 py-3 rounded-xl border border-gray-200 text-gray-900 outline-none
                         focus:border-black focus:ring-2 focus:ring-black/10"
            />
            {errors.fullName && (
              <p className="text-red text-xs mt-1 font-medium">{errors.fullName}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="text-sm font-semibold text-gray-800">Email Address</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="you@example.com"
              className="w-full mt-1 px-4 py-3 rounded-xl border border-gray-200 text-gray-900 outline-none
                         focus:border-black focus:ring-2 focus:ring-black/10"
            />
            {errors.email && (
              <p className="text-red text-xs mt-1 font-medium">{errors.email}</p>
            )}
          </div>

          {/* Phone */}
          <div>
            <label className="text-sm font-semibold text-gray-800">Phone Number</label>
            <input
              type="tel"
              name="telephone"
              value={formData.telephone}
              onChange={handleChange}
              placeholder="+234 800 000 0000"
              className="w-full mt-1 px-4 py-3 rounded-xl border border-gray-200 text-gray-900 outline-none
                         focus:border-black focus:ring-2 focus:ring-black/10"
            />
            {errors.telephone && (
              <p className="text-red text-xs mt-1 font-medium">{errors.telephone}</p>
            )}
          </div>

          {/* Password */}
          <div>
            <label className="text-sm font-semibold text-gray-800">Password</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="********"
                className="w-full mt-1 px-4 py-3 pr-12 rounded-xl border border-gray-200 text-gray-900 outline-none
                           focus:border-black focus:ring-2 focus:ring-black/10"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute top-1/2 right-3 -translate-y-1/2 text-gray-700 hover:text-black"
                aria-label="Toggle password visibility"
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            {errors.password && (
              <p className="text-red text-xs mt-1 font-medium">{errors.password}</p>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <label className="text-sm font-semibold text-gray-800">Confirm Password</label>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="********"
                className="w-full mt-1 px-4 py-3 pr-12 rounded-xl border border-gray-200 text-gray-900 outline-none
                           focus:border-black focus:ring-2 focus:ring-black/10"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword((s) => !s)}
                className="absolute top-1/2 right-3 -translate-y-1/2 text-gray-700 hover:text-black"
                aria-label="Toggle confirm password visibility"
              >
                {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="text-red text-xs mt-1 font-medium">{errors.confirmPassword}</p>
            )}
          </div>

          {/* Terms */}
          <div className="flex items-start gap-2">
            <input
              type="checkbox"
              name="agree"
              checked={formData.agree}
              onChange={handleChange}
              className="mt-1 h-4 w-4 accent-red"
            />
            <p className="text-sm text-gray-600 leading-relaxed">
              I agree to the{" "}
              <Link to="/terms" className="text-red font-semibold hover:underline">
                Terms and Conditions
              </Link>{" "}
              and{" "}
              <Link to="/privacy" className="text-red font-semibold hover:underline">
                Privacy Policy
              </Link>
            </p>
          </div>
          {errors.agree && <p className="text-red text-xs font-medium">{errors.agree}</p>}

          {/* Server error */}
          {isError && (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
              <p className="text-red text-sm font-semibold text-center">
                {error.message}
              </p>
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={isPending}
            className={`w-full py-3 rounded-xl font-semibold transition
              ${isPending ? "bg-gray-300 text-gray-700 cursor-not-allowed" : "bg-red text-white hover:opacity-90"}`}
          >
            {isPending ? "Creating Account..." : "Create Account"}
          </button>
        </form>

        {/* Divider */}
        <div className="flex items-center gap-3 my-6">
          <div className="flex-1 h-px bg-gray-200" />
          <span className="text-sm text-gray-500">Or continue with</span>
          <div className="flex-1 h-px bg-gray-200" />
        </div>

        {/* Google */}
        {/* <button
          type="button"
          onClick={googleAuth}
          className="w-full border border-gray-200 py-3 rounded-xl flex items-center justify-center gap-2 hover:bg-gray-50 transition font-semibold"
        >
          <FaGoogle className="text-red" /> Google
        </button> */}

        {/* Already have account */}
        <p className="text-center mt-4 text-sm text-gray-700">
          Already have an account?{" "}
          <Link to="/signin" className="text-red font-semibold hover:underline">
            Sign In
          </Link>
        </p>

        {/* Back */}
        <div className="mt-6 flex justify-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-red hover:underline font-semibold"
          >
            <FaCircleChevronLeft className="text-red" />
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Signup;
